import { useState, useEffect } from "react";
import to from "await-to-js";
import JDIPrice from "@/common-components-src/js/price/index";
import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKMaskLoading from "@/common-components-src/components/UKMaskLoading/index";
import { DialogContent, DialogTitle, Popover } from "@mui/material";
import { observer } from "mobx-react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import CommonButton from "../commonButton";
import EmptyPage from "../emptyPage";
import styles from "./index.module.scss";
import { fetchInvoiceList } from "@/app/csr-src/apis/order/order.api";
import { InvoiceListDTO, InvoiceListVo } from "@/app/csr-src/apis/order/order.interface";
import { useFileSaver } from "../../utils/saveFile";

interface invoiceModalProps {
  // invoiceList: InvoiceListVo[];
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
}

const invoiceModal: React.FC<invoiceModalProps> = observer(({ orderId, isOpen, closeHandler }) => {
  const { t } = useTranslation();

  const [invoiceList, setInvoiceList] = useState<InvoiceListDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { saveFile } = useFileSaver();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [curItem, setCurItem] = useState<InvoiceListVo | null>(null);

  const downPopoverOpen = Boolean(anchorEl);

  const loadCancelList = async () => {
    UKMaskLoading.show();
    const [err, invoiceListData] = await to(fetchInvoiceList({ orderId }));

    if (err) {
      setError("Failed to fetch order details");
      setInvoiceList(null);
    } else {
      // 开票中提示
      if (!invoiceListData?.invoiceItemVOS?.length) {
        UKToast.show({
          content: t("order.orderList.orderItems.invoicingTips"), // 弹窗内容
          icon: "info", // 可选：success, fail, loading, warn, info，影响图标
          duration: 3500, // 不写的话默认3000
        });
        // 解决第二次点击不生效的问题
        setTimeout(() => {
          closeHandler && closeHandler();
        }, 3500);
      } else {
        setInvoiceList(invoiceListData as InvoiceListDTO);
      }
      setError(null);
    }
    UKMaskLoading.close();
    setLoading(false);
  };

  useEffect(() => {
    loadCancelList();
  }, []);

  // 复制订单号/物流单号
  const copyHandler = () => {
    UKToast.show({
      content: t("order.orderDetail.common.copySuccessTips"), // 弹窗内容
      icon: "success", // 可选：success, fail, loading, warn, info，影响图标
      duration: 2000, // 不写的话默认3000
    });
  };

  const downloadHandler = (url: string, type: string) => {
    handleClose();
    url && saveFile(url, type);
  };

  const viewHandler = (pdfUrl: string) => {
    pdfUrl && window.open(pdfUrl);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  // 异常兜底处理
  if (error) {
    UKToast.show({
      content: "Network issue. Please refresh or try again later.", // 弹窗内容
      icon: "warning", // 可选：success, fail, loading, warn, info，影响图标
    });
    // 解决第二次点击不生效的问题
    setTimeout(() => {
      closeHandler && closeHandler();
    }, 3000);
    return;
  }

  return (
    !loading &&
    invoiceList?.invoiceItemVOS?.length && (
      <UKDialog
        open={isOpen}
        onClose={closeHandler}
        showCloseBtn={true}
        className={styles.repayOrderModal}
        sx={{
          "& .MuiDialog-paper": {
            width: "680px", // 设置自定义宽度
          },
        }}
      >
        <DialogTitle>Invoice</DialogTitle>
        <DialogContent>
          <div className={styles.invoice_content}>
            <div className={styles.order_info_wrap}>
              <span>{t("order.orderDetail.invoiceModal.orderId")}</span>
              <span>
                {orderId}
                <CopyToClipboard text={orderId} onCopy={() => copyHandler()}>
                  <span className={styles.copy_btn}>{t("order.orderDetail.common.copyText")}</span>
                </CopyToClipboard>
              </span>
            </div>
            {invoiceList?.invoiceItemVOS?.length ? (
              <ul className={styles.invoice_list}>
                {invoiceList?.invoiceItemVOS?.map((item: InvoiceListVo) => {
                  return (
                    <li className={styles.invoice_item} key={item.invoiceNo}>
                      <div className={styles.invoice_item_content}>
                        <div>{item.invoiceNoTitle}</div>
                        <div>
                          {item.invoiceNo}
                          {item.invoiceNo && (
                            <CopyToClipboard text={item.invoiceNo} onCopy={() => copyHandler()}>
                              <span className={styles.copy_btn}>{t("order.orderDetail.common.copyText")}</span>
                            </CopyToClipboard>
                          )}
                        </div>
                      </div>
                      <div className={styles.invoice_item_content}>
                        <div>{t("order.orderDetail.invoiceModal.invoiceAmount")}</div>
                        <div>
                          <JDIPrice
                            symbol={invoiceList?.localInfo?.currencySymbol}
                            price={item.totalPaid}
                            uiType={2}
                            containerStyle={{
                              fontSize: "16px",
                              color: "#1A1A1A",
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.invoice_item_content}>
                        <div>{t("order.orderDetail.invoiceModal.invoiceDate")}</div>
                        <div>{item.invoiceTime}</div>
                      </div>
                      <div className={styles.btn_wrap}>
                        <CommonButton
                          variant="outlined"
                          style={{ background: "#FFF" }}
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            setAnchorEl(event.currentTarget);
                            setCurItem(item);
                          }}
                        >
                          {t("order.orderDetail.invoiceModal.downloadBtnText")}
                        </CommonButton>
                        <CommonButton
                          variant="outlined"
                          style={{ background: "#FFF", marginLeft: "12px" }}
                          onClick={() => {
                            viewHandler(item.pdfUrl);
                          }}
                        >
                          {t("order.orderDetail.invoiceModal.viewDetailsBtnText")}
                        </CommonButton>
                      </div>
                      {downPopoverOpen && (
                        <Popover
                          open={downPopoverOpen}
                          anchorEl={anchorEl}
                          onClose={handleClose}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                        >
                          <div className={styles.invoice_download_wrap}>
                            <div className={styles.invoice_download_item} onClick={() => downloadHandler(curItem?.pdfUrl || "", "pdf")}>
                              {t("order.orderDetail.invoiceModal.downloadPdf")}
                            </div>
                            <div className={styles.invoice_download_item} onClick={() => downloadHandler(curItem?.picUrl || "", "image")}>
                              {t("order.orderDetail.invoiceModal.downloadImage")}
                            </div>
                          </div>
                        </Popover>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyPage externalStyles={{ padding: "20px 0" }} />
            )}
          </div>
        </DialogContent>
      </UKDialog>
    )
  );
});

export default invoiceModal;
